import {
	JourneyFilterState,
	JourneyInteraction,
	JourneyMeta,
	JourneyPath,
	JourneyTransition,
} from '../../../models/journey-explorer/journey-explorer';
import { COLORS, MapMode } from './journey-explorer-utils';

export const BOX_HEIGHT = 48;
export const DEFAULT_VIEW_BOX: ViewBox = { x: 0, y: 0, w: 1230, h: 430 };
const MIN_VIEW_WIDTH = DEFAULT_VIEW_BOX.w / 5;
const MAX_VIEW_WIDTH = DEFAULT_VIEW_BOX.w / 0.25;

const ROW_GAP = 92;
const ORIGIN_X = 30;
const ORIGIN_Y = 24;
const FALLBACK_ROWS = 4;
const SAME_COLUMN_DISTANCE = 40;
const BACKWARD_DEPTH = 38;
const FIT_PADDING = 30;
const COLUMN_SPACING = 88;
const ORDER_SWEEPS = 6;
const STRONG_LINK_SHARE = 0.2;
const MIN_ARC_LIFT = 34;
const MAX_ARC_LIFT = 70;
const CORNER_RADIUS = 10;
const WRAP_ENTRY = 18;
const MAX_BANDS = 6;
const BAND_GAP = 70;
const BAND_PENALTY = 0.35;
const MAX_STRETCH = 2.2;
export const DEFAULT_ASPECT = DEFAULT_VIEW_BOX.w / DEFAULT_VIEW_BOX.h;

export interface Point {
	x: number;
	y: number;
}

export interface ViewBox extends Point {
	w: number;
	h: number;
}

export interface InteractionInfo {
	name: string;
	label: string;
	type: string;
	color: string;
	order: number;
}

export interface MapNode extends Point {
	info: InteractionInfo;
	width: number;
	stat: JourneyInteraction;
}

export interface MapEdge {
	key: string;
	from: MapNode;
	to: MapNode;
	stat: JourneyTransition;
	path: string;
	label: Point;
	anchor: 'start' | 'middle';
	backward: boolean;
}

export const edgeKey = (from: string, to: string): string => `${from}\u0000${to}`;

export const BOX_WIDTH = 200;
const LABEL_CHARS = 22;

export const boxLabel = (label: string): string =>
	label.length > LABEL_CHARS ? `${label.slice(0, LABEL_CHARS - 1)}…` : label;

export const buildInteractionLookup = (meta: JourneyMeta): Map<string, InteractionInfo> => {
	const lookup = new Map<string, InteractionInfo>();
	meta.interaction_types.forEach((type) =>
		type.interactions.forEach((interaction) =>
			lookup.set(interaction.name, {
				name: interaction.name,
				label: interaction.label || interaction.name,
				type: type.type,
				color: type.color || COLORS.fallbackType,
				order: lookup.size,
			})
		)
	);
	return lookup;
};

export const resolveInfo = (
	lookup: Map<string, InteractionInfo>,
	name: string,
	type = '',
	color: string = COLORS.fallbackType
): InteractionInfo => lookup.get(name) ?? { name, label: name, type, color, order: Number.MAX_SAFE_INTEGER };

// Walks the busiest links first from the steps most journeys start at, so links back to a step already on
// the current walk are the ones that loop back. Returns the steps in visiting order and the links that
// move forward.
const forwardLinks = (
	names: string[],
	links: JourneyTransition[],
	starts: Map<string, number>
): { order: string[]; forward: JourneyTransition[] } => {
	const outgoing = new Map<string, JourneyTransition[]>();
	const inflow = new Map<string, number>();
	const outflow = new Map<string, number>();
	links.forEach((link) => {
		outgoing.set(link.from, [...(outgoing.get(link.from) ?? []), link]);
		inflow.set(link.to, (inflow.get(link.to) ?? 0) + link.customers);
		outflow.set(link.from, (outflow.get(link.from) ?? 0) + link.customers);
	});
	outgoing.forEach((items) => items.sort((a, b) => b.customers - a.customers));
	const entry = (name: string) => (outflow.get(name) ?? 0) - (inflow.get(name) ?? 0);
	const roots = names
		.map((name, index) => ({ name, index }))
		.sort(
			(a, b) =>
				(starts.get(b.name) ?? 0) - (starts.get(a.name) ?? 0) ||
				entry(b.name) - entry(a.name) ||
				a.index - b.index
		)
		.map((item) => item.name);

	const state = new Map<string, 'walking' | 'done'>();
	const order: string[] = [];
	const forward: JourneyTransition[] = [];
	const walk = (name: string) => {
		state.set(name, 'walking');
		order.push(name);
		(outgoing.get(name) ?? []).forEach((link) => {
			const seen = state.get(link.to);
			if (seen === 'walking') {
				return;
			}
			forward.push(link);
			if (!seen) {
				walk(link.to);
			}
		});
		state.set(name, 'done');
	};
	roots.forEach((name) => !state.has(name) && walk(name));
	return { order, forward };
};

// Each step sits one column after its furthest strong predecessor. Weak side links are ignored here so
// a handful of customers cannot drag a busy step far to the right.
const columnsOf = (order: string[], forward: JourneyTransition[]): Map<string, number> => {
	const incoming = new Map<string, JourneyTransition[]>();
	forward.forEach((link) => incoming.set(link.to, [...(incoming.get(link.to) ?? []), link]));
	const position = new Map(order.map((name, index): [string, number] => [name, index]));
	const outgoing = new Map<string, string[]>();
	forward.forEach((link) => outgoing.set(link.from, [...(outgoing.get(link.from) ?? []), link.to]));

	const pending = new Map(order.map((name): [string, number] => [name, incoming.get(name)?.length ?? 0]));
	const queue = order.filter((name) => pending.get(name) === 0);
	const topological: string[] = [];
	while (queue.length) {
		queue.sort((a, b) => (position.get(a) ?? 0) - (position.get(b) ?? 0));
		const name = queue.shift() as string;
		topological.push(name);
		(outgoing.get(name) ?? []).forEach((to) => {
			pending.set(to, (pending.get(to) ?? 0) - 1);
			if (pending.get(to) === 0) {
				queue.push(to);
			}
		});
	}

	const column = new Map<string, number>();
	topological.forEach((name) => {
		const parents = incoming.get(name) ?? [];
		const strongest = Math.max(0, ...parents.map((link) => link.customers));
		const strong = parents.filter((link) => link.customers >= strongest * STRONG_LINK_SHARE);
		column.set(name, strong.length ? Math.max(...strong.map((link) => (column.get(link.from) ?? 0) + 1)) : 0);
	});
	return column;
};

const flowLayout = (
	names: string[],
	transitions: JourneyTransition[],
	width: (name: string) => number,
	originX: number,
	starts: Map<string, number>,
	aspect: number
): Map<string, Point> => {
	const nameSet = new Set(names);
	const links = transitions.filter((item) => item.from !== item.to && nameSet.has(item.from) && nameSet.has(item.to));
	const weights = new Map<string, Map<string, number>>();
	links.forEach(({ from, to, customers }) => weights.set(from, (weights.get(from) ?? new Map()).set(to, customers)));

	const { order, forward } = forwardLinks(names, links, starts);
	const column = columnsOf(order, forward);

	const columns: string[][] = [];
	order.forEach((name) => {
		const index = column.get(name) ?? 0;
		columns[index] = [...(columns[index] ?? []), name];
	});
	const filled = columns.filter((items) => items?.length);

	const row = new Map<string, number>();
	const setRows = () => filled.forEach((items) => items.forEach((name, index) => row.set(name, index)));
	setRows();
	const neighbourWeight = (a: string, b: string): number =>
		(weights.get(a)?.get(b) ?? 0) + (weights.get(b)?.get(a) ?? 0);
	for (let sweep = 0; sweep < ORDER_SWEEPS; sweep++) {
		filled.forEach((items, index) => {
			const neighbours = [...(filled[index - 1] ?? []), ...(filled[index + 1] ?? [])];
			const centre = (name: string): number => {
				const total = neighbours.reduce((sum, other) => sum + neighbourWeight(name, other), 0);
				return total
					? neighbours.reduce((sum, other) => sum + neighbourWeight(name, other) * (row.get(other) ?? 0), 0) /
							total
					: row.get(name) ?? 0;
			};
			const centres = new Map(items.map((name): [string, number] => [name, centre(name)]));
			items.sort((a, b) => (centres.get(a) ?? 0) - (centres.get(b) ?? 0));
			setRows();
		});
	}

	const shape = filled.map((items) => ({ width: Math.max(...items.map(width)), rows: items.length }));
	const bands = bandsOf(shape, aspect);
	const { row: rowGap, band: bandGap, column: columnGap } = stretchTo(bands, shape, aspect);

	const positions = new Map<string, Point>();
	let y = ORIGIN_Y;
	bands.forEach((band) => {
		const tallest = Math.max(...band.map((index) => shape[index].rows));
		let x = originX;
		band.forEach((index) => {
			const items = filled[index];
			const offset = ((tallest - items.length) * rowGap) / 2;
			items.forEach((name, row) => positions.set(name, { x, y: y + offset + row * rowGap }));
			x += shape[index].width + columnGap;
		});
		y += (tallest - 1) * rowGap + BOX_HEIGHT + bandGap;
	});
	return positions;
};

interface Spacing {
	row: number;
	band: number;
	column: number;
}

type ColumnShape = { width: number; rows: number };

const BASE_SPACING: Spacing = { row: ROW_GAP, band: BAND_GAP, column: COLUMN_SPACING };

const bandWidth = (band: number[], columns: ColumnShape[], column: number): number =>
	band.reduce((sum, index) => sum + columns[index].width, 0) + (band.length - 1) * column;

const tallestIn = (band: number[], columns: ColumnShape[]): number =>
	Math.max(...band.map((index) => columns[index].rows));

const extent = (bands: number[][], columns: ColumnShape[], spacing: Spacing): { w: number; h: number } => ({
	w: Math.max(...bands.map((band) => bandWidth(band, columns, spacing.column))),
	h:
		bands.reduce((sum, band) => sum + (tallestIn(band, columns) - 1) * spacing.row + BOX_HEIGHT, 0) +
		(bands.length - 1) * spacing.band,
});

// Long journeys wrap onto further rows, like lines of text, so the map keeps roughly the canvas shape
// instead of shrinking into a thin strip.
const bandsOf = (columns: ColumnShape[], aspect: number): number[][] => {
	const total = columns.reduce((sum, column) => sum + column.width + COLUMN_SPACING, 0);
	let best: number[][] = [columns.map((_, index) => index)];
	let bestScore = Infinity;
	for (let count = 1; count <= Math.min(MAX_BANDS, columns.length); count++) {
		const bands: number[][] = [[]];
		let used = 0;
		columns.forEach(({ width }, index) => {
			const step = width + COLUMN_SPACING;
			const current = bands[bands.length - 1];
			if (current.length && used + step / 2 > (total * bands.length) / count && bands.length < count) {
				bands.push([index]);
			} else {
				current.push(index);
			}
			used += step;
		});
		const { w, h } = extent(bands, columns, BASE_SPACING);
		const score = Math.abs(Math.log(w / h / aspect)) + (bands.length - 1) * BAND_PENALTY;
		if (score < bestScore) {
			best = bands;
			bestScore = score;
		}
	}
	return best;
};

// Widens the gaps (never the boxes) until the map matches the canvas shape, so spare canvas becomes
// breathing room for the lines and their labels instead of empty margin.
const stretchTo = (bands: number[][], columns: ColumnShape[], aspect: number): Spacing => {
	const base = extent(bands, columns, BASE_SPACING);
	const clamp = (value: number) => Math.min(MAX_STRETCH, Math.max(1, Number.isFinite(value) ? value : 1));
	if (base.w / base.h > aspect) {
		const boxes = bands.reduce((sum, band) => sum + tallestIn(band, columns) * BOX_HEIGHT, 0);
		const free = base.h - boxes;
		const scale = free > 0 ? clamp((base.w / aspect - boxes) / free) : 1;
		return { ...BASE_SPACING, row: BOX_HEIGHT + (ROW_GAP - BOX_HEIGHT) * scale, band: BAND_GAP * scale };
	}
	const widest = bands.reduce((a, b) =>
		bandWidth(b, columns, COLUMN_SPACING) > bandWidth(a, columns, COLUMN_SPACING) ? b : a
	);
	const free = (widest.length - 1) * COLUMN_SPACING;
	const boxes = base.w - free;
	const scale = free > 0 ? clamp((base.h * aspect - boxes) / free) : 1;
	return { ...BASE_SPACING, column: COLUMN_SPACING * scale };
};

export const defaultPositions = (
	meta: JourneyMeta,
	names: string[],
	transitions: JourneyTransition[] = [],
	starts: Map<string, number> = new Map(),
	aspect: number = DEFAULT_ASPECT
): Map<string, Point> => {
	const configured: string[] = meta.interaction_types.flatMap((type) =>
		type.interactions.map((interaction) => interaction.name)
	);

	// Saved x/y in the config is ignored: it knows nothing of groups or which steps are
	// showing, so it leaves boxes overlapping or far apart.
	const positions = new Map<string, Point>();

	const lookup = buildInteractionLookup(meta);
	const unplaced = Array.from(new Set([...configured, ...names])).sort(
		(a, b) => resolveInfo(lookup, a).order - resolveInfo(lookup, b).order || a.localeCompare(b)
	);

	const active = new Set(names);
	const flowing = unplaced.filter((name) => active.has(name));
	flowLayout(flowing, transitions, () => BOX_WIDTH, ORIGIN_X, starts, aspect).forEach((point, name) =>
		positions.set(name, point)
	);

	const flowRight = Math.max(ORIGIN_X, ...flowing.map((name) => (positions.get(name)?.x ?? 0) + BOX_WIDTH));
	unplaced
		.filter((name) => !active.has(name))
		.forEach((name, index) =>
			positions.set(name, {
				x: flowRight + COLUMN_SPACING + (BOX_WIDTH + COLUMN_SPACING) * Math.floor(index / FALLBACK_ROWS),
				y: ORIGIN_Y + ROW_GAP * (index % FALLBACK_ROWS),
			})
		);
	return positions;
};

const sliceTop = <T>(items: T[], customers: (item: T) => number, pct: number): T[] =>
	[...items].sort((a, b) => customers(b) - customers(a)).slice(0, Math.ceil((items.length * pct) / 100));

export const journeyStarts = (paths: JourneyPath[]): Map<string, number> => {
	const starts = new Map<string, number>();
	paths.forEach(
		(path) => path.steps.length && starts.set(path.steps[0], (starts.get(path.steps[0]) ?? 0) + path.customers)
	);
	return starts;
};

export const visibleInteractions = (interactions: JourneyInteraction[], pct: number): JourneyInteraction[] =>
	sliceTop(interactions, (item) => item.customers, pct);

export const visibleTransitions = (
	transitions: JourneyTransition[],
	visibleNames: Set<string>,
	pct: number
): JourneyTransition[] =>
	sliceTop(
		transitions.filter((item) => item.from !== item.to && visibleNames.has(item.from) && visibleNames.has(item.to)),
		(item) => item.customers,
		pct
	);

const blocks = (obstacles: MapNode[], from: MapNode, to: MapNode, left: number, right: number) =>
	obstacles.some(
		(node) =>
			node !== from &&
			node !== to &&
			Math.abs(node.y - from.y) < BOX_HEIGHT &&
			node.x < right &&
			node.x + node.width > left
	);

export const routeEdge = (
	from: MapNode,
	to: MapNode,
	obstacles: MapNode[] = []
): Pick<MapEdge, 'path' | 'label' | 'anchor' | 'backward'> => {
	const fromCenter = from.x + from.width / 2;
	const toCenter = to.x + to.width / 2;

	if (Math.abs(fromCenter - toCenter) < SAME_COLUMN_DISTANCE) {
		const downwards = to.y > from.y;
		const startY = downwards ? from.y + BOX_HEIGHT : from.y;
		const endY = downwards ? to.y : to.y + BOX_HEIGHT;
		const midY = (startY + endY) / 2;
		return {
			path: `M${fromCenter},${startY} C${fromCenter},${midY} ${toCenter},${midY} ${toCenter},${endY}`,
			label: { x: (fromCenter + toCenter) / 2 + 8, y: midY },
			anchor: 'start',
			backward: false,
		};
	}

	if (to.x > from.x + from.width) {
		const startX = from.x + from.width;
		const startY = from.y + BOX_HEIGHT / 2;
		const endX = to.x;
		const endY = to.y + BOX_HEIGHT / 2;
		const bend = startX + (endX - startX) / 2;
		if (Math.abs(endY - startY) < 4 && blocks(obstacles, from, to, startX, endX)) {
			const lift = Math.min(MAX_ARC_LIFT, MIN_ARC_LIFT + (endX - startX) * 0.04);
			return {
				path: `M${fromCenter},${from.y} C${fromCenter},${from.y - lift} ${toCenter},${
					to.y - lift
				} ${toCenter},${to.y}`,
				label: { x: (fromCenter + toCenter) / 2, y: from.y - lift * 0.75 },
				anchor: 'middle',
				backward: false,
			};
		}
		return {
			path:
				Math.abs(endY - startY) < 4
					? `M${startX},${startY} L${endX},${endY}`
					: `M${startX},${startY} C${bend},${startY} ${bend},${endY} ${endX},${endY}`,
			label: { x: bend, y: (startY + endY) / 2 },
			anchor: 'middle',
			backward: false,
		};
	}

	const below = to.y >= from.y + BOX_HEIGHT + SAME_COLUMN_DISTANCE;
	const above = to.y + BOX_HEIGHT + SAME_COLUMN_DISTANCE <= from.y;
	if (below && to.x < from.x) {
		const r = CORNER_RADIUS;
		const startY = from.y + BOX_HEIGHT;
		const turnY = (startY + to.y) / 2;
		const entryX = to.x - WRAP_ENTRY;
		const endY = to.y + BOX_HEIGHT / 2;
		return {
			path:
				`M${fromCenter},${startY} L${fromCenter},${turnY - r} Q${fromCenter},${turnY} ${
					fromCenter - r
				},${turnY} ` +
				`L${entryX + r},${turnY} Q${entryX},${turnY} ${entryX},${turnY + r} ` +
				`L${entryX},${endY - r} Q${entryX},${endY} ${entryX + r},${endY} L${to.x},${endY}`,
			label: { x: (fromCenter + entryX) / 2, y: turnY },
			anchor: 'middle',
			backward: false,
		};
	}

	if (below || above) {
		const startY = below ? from.y + BOX_HEIGHT : from.y;
		const endY = below ? to.y : to.y + BOX_HEIGHT;
		const midY = (startY + endY) / 2;
		return {
			path: `M${fromCenter},${startY} C${fromCenter},${midY} ${toCenter},${midY} ${toCenter},${endY}`,
			label: { x: (fromCenter + toCenter) / 2, y: midY },
			anchor: 'middle',
			backward: false,
		};
	}

	const depth = Math.max(from.y, to.y) + BOX_HEIGHT + BACKWARD_DEPTH;
	return {
		path: `M${fromCenter},${from.y + BOX_HEIGHT} C${fromCenter},${depth} ${toCenter},${depth} ${toCenter},${
			to.y + BOX_HEIGHT
		}`,
		label: { x: (fromCenter + toCenter) / 2, y: depth - 8 },
		anchor: 'middle',
		backward: true,
	};
};

export const interactionSecs = (stat: JourneyInteraction, mode: MapMode): number | null =>
	mode === 'median' ? stat.median_secs_to_next : stat.avg_secs_to_next;

export const transitionSecs = (stat: JourneyTransition, mode: MapMode): number | null =>
	mode === 'median' ? stat.median_secs : stat.avg_secs;

export const interactionMeasure = (stat: JourneyInteraction, mode: MapMode): number =>
	mode === 'customers' ? stat.customers : interactionSecs(stat, mode) ?? 0;

export const transitionMeasure = (stat: JourneyTransition, mode: MapMode): number =>
	mode === 'customers' ? stat.customers : transitionSecs(stat, mode) ?? 0;

const ratio = (value: number, max: number, mode: MapMode): number => {
	if (max <= 0) {
		return 0;
	}
	return mode === 'customers' ? value / max : Math.log1p(value) / Math.log1p(max);
};

export const borderWidth = (value: number, max: number, mode: MapMode): number => {
	const share = ratio(value, max, mode);
	return 1 + 4 * (mode === 'customers' ? Math.sqrt(share) : share);
};

export const lineWidth = (value: number, max: number, mode: MapMode): number =>
	Math.min(7, 1.2 + 5.8 * ratio(value, max, mode));

export const fitViewBox = (nodes: MapNode[], edges: MapEdge[]): ViewBox | null => {
	if (!nodes.length) {
		return null;
	}
	const left = Math.min(...nodes.map((node) => node.x)) - FIT_PADDING;
	const top = Math.min(...nodes.map((node) => node.y)) - FIT_PADDING;
	const right = Math.max(...nodes.map((node) => node.x + node.width)) + FIT_PADDING;
	const bottom = Math.max(
		...nodes.map((node) => node.y + BOX_HEIGHT),
		...edges.filter((edge) => edge.backward).map((edge) => edge.label.y + 16)
	);
	return { x: left, y: top, w: right - left, h: bottom + FIT_PADDING - top };
};

export const zoomViewBox = (viewBox: ViewBox, factor: number, center: Point): ViewBox => {
	const width = Math.min(MAX_VIEW_WIDTH, Math.max(MIN_VIEW_WIDTH, viewBox.w * factor));
	const scale = width / viewBox.w;
	return {
		x: center.x - (center.x - viewBox.x) * scale,
		y: center.y - (center.y - viewBox.y) * scale,
		w: width,
		h: viewBox.h * scale,
	};
};

export const isInteractionFiltered = (state: JourneyFilterState, name: string): boolean =>
	state.interaction_filters.some((filter) => filter.interaction_name === name);

export const isTransitionFiltered = (state: JourneyFilterState, from: string, to: string): boolean =>
	state.transition_filters.some((filter) => filter.from === from && filter.to === to);
